import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import PlacesAutocomplete, { geocodeByAddress, geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import AuthLayout from './AuthLayout';
import Carousel from './Carousel';
import { paymentService, userService } from '../../_services';
import siteSetting from '../../config/env';
import './auth.css';

const stripePromise = loadStripe(siteSetting.stripeKey);

// Use price IDs from config (handles test vs live mode automatically)
const PRICE_IDS = {
  salonMonthly: siteSetting.stripe.salonMonthlyPriceId,
  salonYearly: siteSetting.stripe.salonYearlyPriceId,
  selfMonthly: siteSetting.stripe.selfMonthlyPriceId,
  selfYearly: siteSetting.stripe.selfYearlyPriceId,
};

// Correct pricing to display (hardcoded to ensure correct display regardless of config)
const PRICING = {
  salon: { monthly: 59, yearly: 599, yearlyPerMonth: '49.92', save: '£109' },
  self: { monthly: 25, yearly: 249, yearlyPerMonth: '20.75', save: '£51' },
};

const PLANS = [
  { id: 'self', title: 'Self-employed', sub: 'Just you, your diary and your clients', points: ['Online booking page', 'Deposits and no-show protection', 'Reminders and marketing'] },
  { id: 'salon', title: 'Salon or barbershop', sub: 'You and your team, any size', points: ['Everything in Self-employed', 'Team diaries, rota and pay', 'Reports, stock and locations'] },
];

const BUSINESS_TYPES = [
  { id: 'hair_salon', label: 'Hair salon' },
  { id: 'barber', label: 'Barbershop' },
  { id: 'beauty_salon', label: 'Beauty' },
  { id: 'nail_salon', label: 'Nails' },
  { id: 'spa', label: 'Spa' },
  { id: 'other', label: 'Other' },
];

const STEPS = ['Your business', 'Type of business', 'Your name', 'Contact details', 'Password', 'Your plan', 'Verify your number'];

const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
];
const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const IE_EIRCODE = /^[A-Z]\d{2}\s*[A-Z\d]{4}$/i;

// PhoneInput keeps E.164; a UK number typed with its trunk zero ("+4407...")
// is normalised so the SMS and the account both get "+447...".
const dropTrunkZero = (v) => (v && v.startsWith('+440') ? `+44${v.slice(4)}` : v);

// Currency follows the business address; GBP unless the address says otherwise.
const currencyFor = (countryShortName) => {
  if (countryShortName === 'IE') return { currency: 'EUR', currencySymbol: '€' };
  if (countryShortName === 'US') return { currency: 'USD', currencySymbol: '$' };
  return { currency: 'GBP', currencySymbol: '£' };
};

const TickIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
const EyeIcon = ({ off }) => off ? (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3l18 18" /><path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" /><path d="M9.9 5.1A10.9 10.9 0 0 1 12 5c5 0 9 4 10 7a11.6 11.6 0 0 1-3.2 4.3" /><path d="M6.6 6.6A11.7 11.7 0 0 0 2 12c1 3 5 7 10 7a9.7 9.7 0 0 0 4.3-1" /></svg>
) : (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12c1-3 5-7 10-7s9 4 10 7c-1 3-5 7-10 7S3 15 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
);
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
);

class ProfessionalSignup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: 1,

      // Step 1: your business. Search Google for the listing (fills name +
      // address); 'manual' is the fallback for businesses not on Google.
      entryMode: 'search', // 'search' | 'manual'
      salonName: '',
      nameFromListing: false,
      businessType: '',
      formattedAddress: '',
      addressPicked: false,   // the current address text is a picked result
      addressPrecise: false,  // ...and it is building-level (not just a street or postcode)
      businessChosen: false,  // something was picked: show the summary card
      editingAddress: false,
      searchFocused: false, // step 1 hero collapses while the search is in use
      // A picked postcode opens a list of businesses at/near it to choose from.
      postcodePick: null, // { postcode, businesses: [], loading }
      // Manual address form (used when the business is not found by search)
      building: '',
      street: '',
      addressLine2: '',
      town: '',
      postcodeManual: '',
      countryCode: 'GB',
      country: 'GB',
      countryName: 'United Kingdom',
      countryShortName: 'GB',
      countryLongName: 'United Kingdom',
      stateCode: '',
      stateName: '',
      cityName: '',
      postalCode: '',
      administrativeAreaLevel1: '',
      administrativeAreaLevel2: '',
      latitude: siteSetting.default_latitude || '54.00000000',
      longitude: siteSetting.default_longitude || '-2.00000000',
      currency: 'GBP',
      currencySymbol: '£',

      // Step 2: about you
      first_name: '',
      last_name: '',
      email: '',
      phoneNumber: '',
      phoneKey: 0,
      password: '',
      showPassword: false,
      passwordStrength: 0,

      // Step 3: plan
      planKind: 'salon', // 'salon' | 'self'
      selectedPlanType: 'yearly', // 'monthly' | 'yearly'
      agree: false,

      // Step 4: SMS code, then /register creates the account and checkout starts
      codeArr: ['', '', '', '', '', ''],
      codeSent: false,
      sendingCode: false,
      verifying: false,
      codeError: '',
      resendCountdown: 0,
      registered: false,

      // Errors
      salonNameError: '',
      businessTypeError: '',
      formattedAddressError: '',
      fNameError: '',
      lNameError: '',
      emailError: '',
      phoneNumberError: '',
      passwordError: '',
      agreeError: '',
      error: '',
      isLoading: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handlePayment = this.handlePayment.bind(this);
    this.handleSendCode = this.handleSendCode.bind(this);
    this.handleVerifyAndRegister = this.handleVerifyAndRegister.bind(this);
    this.codeInputs = [];
  }

  componentDidMount() {
    // Mobile browsers: the on-screen keyboard shrinks the visual viewport but
    // not the layout, so a fixed bar ends up behind the keys. Track the
    // visual viewport and lift the action bar (and the space reserved for it)
    // by the keyboard height. In the Capacitor app the webview itself resizes,
    // so the offset stays 0 there.
    const vv = window.visualViewport;
    if (!vv) return;
    this._onViewport = () => {
      const keyboard = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      const root = this._rootEl;
      if (!root) return;
      root.style.setProperty('--auth-kb', `${keyboard}px`);
      root.classList.toggle('kb-open', keyboard > 80);
    };
    vv.addEventListener('resize', this._onViewport);
    vv.addEventListener('scroll', this._onViewport);
    this._onViewport();
  }

  componentWillUnmount() {
    if (this._resendTimer) clearInterval(this._resendTimer);
    const vv = window.visualViewport;
    if (vv && this._onViewport) { vv.removeEventListener('resize', this._onViewport); vv.removeEventListener('scroll', this._onViewport); }
  }

  /* ---------------- field handling ---------------- */

  handleChange(e) {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    this.setState({ [name]: newValue, [`${name}Error`]: '', error: '' });
    if (name === 'password') this.checkPasswordStrength(value);
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());
  }

  checkPasswordStrength(password) {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    this.setState({ passwordStrength: strength });
  }

  getPasswordStrengthClass() {
    const { passwordStrength } = this.state;
    if (passwordStrength <= 1) return 'weak';
    if (passwordStrength <= 2) return 'medium';
    return 'strong';
  }

  // --- Address search (Google Places) ---
  handleAddressChange = (address) => {
    this.setState({ formattedAddress: address, addressPicked: false, formattedAddressError: '' });
  };

  // Copy the parts of a geocoder result the API wants (country, postcode,
  // town, region, coordinates) into state.
  applyGeocodeResult = (r, fallbackAddress) => {
    const comp = (type, key = 'long_name') => { const c = (r.address_components || []).find((x) => x.types.includes(type)); return c ? c[key] : ''; };
    const countryShortName = comp('country', 'short_name');
    this.setState({
      formattedAddress: r.formatted_address || fallbackAddress,
      addressPicked: true,
      countryShortName: countryShortName || 'GB',
      countryLongName: comp('country') || 'United Kingdom',
      country: countryShortName || 'GB',
      countryName: comp('country') || 'United Kingdom',
      postalCode: comp('postal_code'),
      cityName: comp('postal_town') || comp('locality'),
      administrativeAreaLevel1: comp('administrative_area_level_1', 'short_name'),
      stateName: comp('administrative_area_level_1'),
      administrativeAreaLevel2: comp('administrative_area_level_2'),
      // Keep the pieces too, so "Edit" opens the address form prefilled.
      building: comp('street_number'),
      street: comp('route'),
      addressLine2: '',
      town: comp('postal_town') || comp('locality'),
      postcodeManual: comp('postal_code'),
      countryCode: countryShortName === 'IE' ? 'IE' : 'GB',
      ...currencyFor(countryShortName),
    });
    return getLatLng(r).then((latLng) => {
      this.setState({
        latitude: latLng.lat ? latLng.lat : siteSetting.default_latitude,
        longitude: latLng.lng ? latLng.lng : siteSetting.default_longitude,
      });
      return latLng;
    });
  };

  // Places gives (description, placeId, suggestion). A business listing
  // ("establishment") carries the business name as its main text; a plain
  // address does not, so the name field stays for them to fill in. A
  // postcode on its own opens a list of businesses at it (see below).
  handleAddressSelect = (address, placeId, suggestion) => {
    const types = (suggestion && Array.isArray(suggestion.types)) ? suggestion.types : [];
    if (types.includes('postal_code')) { this.pickPostcode(address); return; }
    const isListing = types.includes('establishment');
    const lookup = placeId ? geocodeByPlaceId(placeId) : geocodeByAddress(address);

    if (!isListing) {
      // A street, area or plain address is not a business: open the address
      // form with whatever the pick tells us already filled in.
      this.setState({ formattedAddress: address });
      lookup
        .then((results) => this.openManualFromGeocode(results[0]))
        .catch(() => this.startManual(''));
      return;
    }

    const listingName = suggestion.formattedSuggestion ? suggestion.formattedSuggestion.mainText : '';
    this.setState({
      formattedAddress: address,
      formattedAddressError: '',
      salonName: listingName,
      nameFromListing: true,
      salonNameError: '',
      postcodePick: null,
      businessChosen: true,
      addressPrecise: true,
      editingAddress: false,
    });
    // Listings resolve best by place id; a text lookup can miss them. If the
    // lookup fails altogether, keep the picked text and treat it as done.
    lookup
      .then((results) => this.applyGeocodeResult(results[0], address))
      .catch((err) => { console.error('Address lookup failed', err); this.setState({ addressPicked: true, formattedAddress: address }); });
  };

  // Prefill the manual address form from a geocoder result.
  openManualFromGeocode = (r) => {
    const comp = (type, key = 'long_name') => { const c = ((r && r.address_components) || []).find((x) => x.types.includes(type)); return c ? c[key] : ''; };
    const country = comp('country', 'short_name');
    this.setState({
      entryMode: 'manual',
      salonName: '', nameFromListing: false,
      formattedAddress: '', addressPicked: false, addressPrecise: false, businessChosen: false, editingAddress: false, postcodePick: null,
      formattedAddressError: '', salonNameError: '',
      building: comp('street_number'),
      street: comp('route'),
      town: comp('postal_town') || comp('locality'),
      postcodeManual: comp('postal_code'),
      countryCode: country === 'IE' ? 'IE' : 'GB',
    });
  };

  // Postcode picked: find hair and beauty businesses at/near it and let them
  // choose, or fall through to typing the address with the postcode in place.
  pickPostcode = (description) => {
    const postcode = (description.match(/[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/i) || [description])[0].toUpperCase();
    this.setState({ formattedAddress: postcode, addressPicked: false, postcodePick: { postcode, businesses: [], loading: true } });
    geocodeByAddress(description)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => new Promise((resolve) => {
        const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
        const location = new window.google.maps.LatLng(latLng.lat, latLng.lng);
        const seen = {}; const out = [];
        let pending = 2;
        const done = () => { pending -= 1; if (pending === 0) resolve(out); };
        ['hair_care', 'beauty_salon'].forEach((type) => {
          svc.nearbySearch({ location, radius: 400, type }, (res, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(res)) {
              res.forEach((pl) => { if (!seen[pl.place_id]) { seen[pl.place_id] = true; out.push({ placeId: pl.place_id, name: pl.name, address: pl.vicinity || '' }); } });
            }
            done();
          });
        });
      }))
      .then((businesses) => this.setState((st) => (st.postcodePick ? { postcodePick: { ...st.postcodePick, businesses: businesses.slice(0, 8), loading: false } } : null)))
      .catch(() => this.setState((st) => (st.postcodePick ? { postcodePick: { ...st.postcodePick, businesses: [], loading: false } } : null)));
  };

  pickNearbyBusiness = (biz) => {
    this.setState({ salonName: biz.name, nameFromListing: true, salonNameError: '', formattedAddress: biz.address, postcodePick: null, businessChosen: true, addressPrecise: true, editingAddress: false });
    geocodeByPlaceId(biz.placeId)
      .then((results) => this.applyGeocodeResult(results[0], biz.address))
      .catch(() => this.setState({ addressPicked: true }));
  };

  /* ---------------- steps ---------------- */

  // Continue stays disabled until a step is complete (works the same on mobile).
  stepComplete(step) {
    const s = this.state;
    if (step === 1) {
      if (s.entryMode === 'manual') return s.salonName.trim().length >= 2 && !!s.street.trim() && !!s.town.trim() && this.manualPostcodeOk();
      return s.salonName.trim().length >= 2 && !!s.formattedAddress.trim();
    }
    if (step === 2) return !!s.businessType;
    if (step === 3) return s.first_name.trim().length >= 2 && s.last_name.trim().length >= 2;
    if (step === 4) return this.validateEmail(s.email) && String(s.phoneNumber || '').replace(/\D/g, '').length >= 10;
    if (step === 5) return s.password.length >= 8;
    if (step === 6) return !!s.agree;
    return true;
  }

  validateStep1() {
    const { salonName, formattedAddress, entryMode } = this.state;
    let ok = true;
    if (!salonName || salonName.trim().length < 2) { this.setState({ salonNameError: 'Enter your business name' }); ok = false; }
    if (entryMode === 'manual') {
      if (!this.state.street.trim() || !this.state.town.trim() || !this.manualPostcodeOk()) { this.setState({ formattedAddressError: 'Enter your street, town and postcode' }); ok = false; }
      else this.composeManualAddress();
    } else if (!formattedAddress || !formattedAddress.trim()) { this.setState({ formattedAddressError: 'Search for your address and pick it from the list' }); ok = false; }
    return ok;
  }

  validateStep(step) {
    const { first_name, last_name, email, phoneNumber, password, agree } = this.state;
    let ok = true;
    if (step === 1) return this.validateStep1();
    if (step === 2) {
      if (!this.state.businessType) { this.setState({ businessTypeError: 'Pick the closest match' }); ok = false; }
    } else if (step === 3) {
      if (!first_name || first_name.trim().length < 2) { this.setState({ fNameError: 'Enter your first name' }); ok = false; }
      if (!last_name || last_name.trim().length < 2) { this.setState({ lNameError: 'Enter your last name' }); ok = false; }
    } else if (step === 4) {
      if (!email) { this.setState({ emailError: 'Enter your email address' }); ok = false; }
      else if (!this.validateEmail(email)) { this.setState({ emailError: 'That email address does not look right' }); ok = false; }
      if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) { this.setState({ phoneNumberError: 'Enter your mobile number. We text a code to it.' }); ok = false; }
    } else if (step === 5) {
      if (!password) { this.setState({ passwordError: 'Create a password' }); ok = false; }
      else if (password.length < 8) { this.setState({ passwordError: 'Use at least 8 characters' }); ok = false; }
    } else if (step === 6) {
      if (!agree) { this.setState({ agreeError: 'Please agree to the terms to continue' }); ok = false; }
    }
    return ok;
  }

  handleNext() {
    const { currentStep } = this.state;
    if (!this.validateStep(currentStep)) return;
    const next = currentStep + 1;
    this.setState({ currentStep: next, error: '' }, () => {
      window.scrollTo(0, 0);
      if (next === STEPS.length && !this.state.codeSent) this.handleSendCode();
    });
  }

  handleBack() {
    const { currentStep, registered, entryMode, businessChosen, postcodePick } = this.state;
    if (registered) return; // account exists now; only forward from here
    if (currentStep === 1) {
      // Inside step 1: manual form or a picked business goes back to the search.
      if (entryMode === 'manual') { this.setState({ entryMode: 'search', formattedAddress: '', addressPicked: false, salonName: '', nameFromListing: false, formattedAddressError: '', salonNameError: '' }); return; }
      if (businessChosen || postcodePick) { this.resetBusinessSearch(); return; }
      this.props.history.push('/signup');
      return;
    }
    this.setState({ currentStep: currentStep - 1, error: '' }, () => window.scrollTo(0, 0));
  }

  /* ---------------- plan + checkout ---------------- */

  getPriceId() {
    const { planKind, selectedPlanType } = this.state;
    if (planKind === 'salon') return selectedPlanType === 'yearly' ? PRICE_IDS.salonYearly : PRICE_IDS.salonMonthly;
    return selectedPlanType === 'yearly' ? PRICE_IDS.selfYearly : PRICE_IDS.selfMonthly;
  }

  // 1 = salon, 2 = self-employed (API user type)
  getUserType() {
    return this.state.planKind === 'salon' ? 1 : 2;
  }

  handlePayment() {
    this.setState({ isLoading: true, error: '' });
    const priceId = this.getPriceId();
    const userType = this.getUserType();
    paymentService.subscriptionCheckout({ priceId, userType })
      .then(async (response) => {
        if (response.data.statusCode === 0) {
          this.setState({ isLoading: false, error: response.data.error?.responseMessage || 'Could not start payment. Please try again.' });
          return;
        }
        const sessionId = response.data?.responseData?.result?.id;
        if (!sessionId) {
          this.setState({ isLoading: false, error: 'Could not start payment. Please try again.' });
          return;
        }
        const stripe = await stripePromise;
        const result = await stripe.redirectToCheckout({ sessionId });
        if (result.error) {
          this.setState({ isLoading: false, error: result.error.message || 'Could not open payment. Please try again.' });
        }
      })
      .catch((err) => {
        this.setState({ isLoading: false, error: this.apiErr(err, 'Could not start payment. Please try again.') });
      });
  }

  // Pull a human message out of either a v2 {statusCode:0,error} body or an axios error.
  apiErr(err, fallback) {
    const d = err && err.response && err.response.data;
    return (d && (d.error?.responseMessage || d.message)) || (err && err.message) || fallback;
  }

  /* ---------------- SMS code + register ---------------- */

  startResendCountdown() {
    if (this._resendTimer) clearInterval(this._resendTimer);
    this.setState({ resendCountdown: 30 });
    this._resendTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendCountdown <= 1) { clearInterval(this._resendTimer); return { resendCountdown: 0 }; }
        return { resendCountdown: prev.resendCountdown - 1 };
      });
    }, 1000);
  }

  // A corrected number gets its code straight away: the resend wait was for
  // the old one, and left the new one on "Sending…" until it ran out.
  wrongNumber = () => {
    if (this._resendTimer) clearInterval(this._resendTimer);
    this.setState({ currentStep: 4, codeSent: false, codeArr: ['', '', '', '', '', ''], codeError: '', resendCountdown: 0 });
  };

  handleSendCode() {
    const { email, phoneNumber, sendingCode, resendCountdown } = this.state;
    if (sendingCode || resendCountdown > 0) return;
    this.setState({ sendingCode: true, codeError: '' });
    userService.sendProSignupCode({ email, phone: phoneNumber })
      .then((resp) => {
        if (resp?.data?.statusCode !== 1) throw new Error(resp?.data?.error?.responseMessage || 'Could not send the code.');
        this.setState({ codeSent: true, sendingCode: false });
        this.startResendCountdown();
      })
      .catch((err) => {
        this.setState({ codeSent: true, sendingCode: false, codeError: this.apiErr(err, 'Could not send the code. Check your number and try again.') });
      });
  }

  buildRegisterPayload() {
    const s = this.state;
    const isSalon = s.planKind === 'salon';
    const fullAddress = [s.formattedAddress, s.cityName, s.stateName, s.countryName]
      .filter(Boolean)
      .filter((part, i, arr) => arr.indexOf(part) === i && !(i > 0 && s.formattedAddress.includes(part)))
      .join(', ');
    return {
      email: s.email.trim(),
      password: s.password,
      phoneNumber: s.phoneNumber,
      first_name: s.first_name.trim(),
      last_name: s.last_name.trim(),
      name: `${s.first_name.trim()} ${s.last_name.trim()}`.trim(),
      isSalonOwner: isSalon ? 1 : 0,
      employmentTypeId: isSalon ? 1 : 2,
      jobTitle: 'Owner',
      // Always a new business: the join-an-existing-salon path is gone.
      salonName: s.salonName.trim(),
      formattedAddress: fullAddress,
      countryShortName: s.countryShortName || s.country,
      countryLongName: s.countryLongName || s.countryName,
      postalCode: s.postalCode,
      administrativeAreaLevel1: s.administrativeAreaLevel1 || s.stateName,
      administrativeAreaLevel2: s.administrativeAreaLevel2,
      latitude: s.latitude,
      longitude: s.longitude,
      currency: s.currency,
      currencySymbol: s.currencySymbol,
      businessType: s.businessType,
    };
  }

  // Verify the SMS code, create the account (register), auto-login, then go
  // straight to Stripe checkout for the plan chosen on step 3.
  handleVerifyAndRegister() {
    const { email } = this.state;
    const code = (this.state.codeArr || []).join('');
    if (!/^\d{6}$/.test(String(code))) { this.setState({ codeError: 'Enter the 6-digit code we texted you.' }); return; }
    this.setState({ verifying: true, codeError: '', error: '' });
    userService.verifyProSignupCode({ email, code })
      .then((resp) => {
        if (resp?.data?.statusCode !== 1) throw new Error(resp?.data?.error?.responseMessage || 'That code is incorrect or expired.');
        return userService.proRegister(this.buildRegisterPayload());
      })
      .then((resp) => {
        if (resp?.data?.statusCode !== 1) throw new Error(resp?.data?.error?.responseMessage || 'Could not create your account.');
        const data = resp?.data?.responseData?.data || resp?.data?.responseData || {};
        const userInfo = data.userInfo || {};
        const tokenInfo = data.tokenInfo || {};
        if (!tokenInfo.accessToken) throw new Error('Could not create your account. Please try again.');
        localStorage.setItem('accessToken', tokenInfo.accessToken);
        if (tokenInfo.refreshToken) localStorage.setItem('refreshToken', tokenInfo.refreshToken);
        if (userInfo.userId) localStorage.setItem('userId', userInfo.userId);
        if (userInfo.salonId) localStorage.setItem('salonId', userInfo.salonId);
        // The legacy /profile endpoint only attaches userInfo.hairdresser (incl
        // isSalonOwner) when defaultRedirectTo===1. Legacy login sets this; the
        // v2 signup must too, or the new owner loads with no hairdresser role.
        localStorage.setItem('defaultRedirectTo', 1);
        this.setState({ verifying: false, registered: true }, () => this.handlePayment());
      })
      .catch((err) => {
        this.setState({ verifying: false, codeError: this.apiErr(err, 'Something went wrong. Please try again.') });
      });
  }

  // --- Segmented 6-digit OTP entry ---
  focusCodeBox = (i) => { const el = this.codeInputs[i]; if (el) el.focus(); };

  handleCodeChange = (i, e) => {
    const v = (e.target.value || '').replace(/\D/g, '');
    const arr = [...this.state.codeArr];
    if (v.length > 1) {
      // paste / autofill into a single box: distribute from here
      const digits = v.slice(0, 6 - i).split('');
      digits.forEach((d, k) => { arr[i + k] = d; });
      this.setState({ codeArr: arr, codeError: '' });
      this.focusCodeBox(Math.min(i + digits.length, 5));
      return;
    }
    arr[i] = v;
    this.setState({ codeArr: arr, codeError: '' });
    if (v && i < 5) this.focusCodeBox(i + 1);
  };

  handleCodeKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !this.state.codeArr[i] && i > 0) {
      const arr = [...this.state.codeArr];
      arr[i - 1] = '';
      this.setState({ codeArr: arr });
      this.focusCodeBox(i - 1);
    }
  };

  handleCodePaste = (e) => {
    const text = ((e.clipboardData && e.clipboardData.getData('text')) || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const arr = ['', '', '', '', '', ''];
    text.split('').forEach((d, k) => { arr[k] = d; });
    this.setState({ codeArr: arr, codeError: '' });
    this.focusCodeBox(Math.min(text.length, 5));
  };

  /* ---------------- render ---------------- */

  renderProgress() {
    const { currentStep } = this.state;
    return (
      <div className="auth-steps" aria-label={`Step ${currentStep} of ${STEPS.length}`}>
        <div className="auth-steps-row">
          <span className="auth-steps-count">Step {currentStep} of {STEPS.length}</span>
          {currentStep < STEPS.length && <span className="auth-steps-next">Next: {STEPS[currentStep]}</span>}
        </div>
        <div className="auth-steps-bar"><div className="auth-steps-fill" style={{ width: `${(currentStep / STEPS.length) * 100}%` }} /></div>
      </div>
    );
  }

  renderAddressSearch({ id, placeholder, businessSearch, autoFocus }) {
    const { formattedAddress, addressPicked, formattedAddressError } = this.state;
    const placesReady = typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places;
    if (!placesReady) {
      const plain = <input type="text" id={id} className={`auth-input ${formattedAddressError ? 'error' : ''}`} placeholder={businessSearch ? placeholder : 'Street, town, postcode'} value={formattedAddress} onChange={(e) => this.handleAddressChange(e.target.value)} autoComplete={businessSearch ? 'organization' : 'street-address'}
        onKeyDown={businessSearch ? (e) => { if (e.key === 'Enter') { e.preventDefault(); this.startManual(formattedAddress); } } : undefined} />;
      if (!businessSearch) return plain;
      // Google hasn't loaded (slow or blocked), so nothing can be listed:
      // typing the details in is the way on, or no one could get past step 1.
      return (
        <>
          {plain}
          <div className="auth-field-hint"><button type="button" className="auth-link-btn" onClick={() => this.startManual(formattedAddress)}>Enter your business details</button></div>
        </>
      );
    }
    return (
      <PlacesAutocomplete
        value={formattedAddress}
        onChange={this.handleAddressChange}
        onSelect={this.handleAddressSelect}
        searchOptions={{ componentRestrictions: { country: ['gb', 'ie'] }, ...(businessSearch ? {} : { types: ['geocode'] }) }}
        debounce={200}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className="auth-addr">
            <span className="auth-addr-icon"><SearchIcon /></span>
            <input {...getInputProps({
              id,
              className: `auth-input auth-input--with-icon ${formattedAddressError ? 'error' : ''}`,
              placeholder,
              autoComplete: 'off',
              autoFocus,
              onFocus: () => { if (businessSearch) this.setState({ searchFocused: true }); },
              onBlur: () => { if (businessSearch && !this.state.formattedAddress.trim()) this.setState({ searchFocused: false }); },
            })} />
            {/* Once picked the menu closes; the library can leave its loading
                flag set after a select, which showed a stuck "Searching…" row. */}
            {!addressPicked && !this.state.postcodePick && (loading || suggestions.length > 0 || (businessSearch && formattedAddress.trim().length >= 3)) && (
              <div className="auth-addr-menu" role="listbox">
                <div className="auth-addr-list">
                {loading && !suggestions.length && <div className="auth-addr-hint">Searching…</div>}
                {!loading && !suggestions.length && businessSearch && <div className="auth-addr-hint">No matches.</div>}
                {suggestions.map((sg) => {
                  const isListing = Array.isArray(sg.types) && sg.types.includes('establishment');
                  return (
                    <div {...getSuggestionItemProps(sg, { className: `auth-addr-item${sg.active ? ' active' : ''}` })} key={sg.placeId} role="option" aria-selected={sg.active}>
                      <span className="auth-addr-main">{sg.formattedSuggestion ? sg.formattedSuggestion.mainText : sg.description}{isListing && businessSearch && <span className="auth-addr-tag">Business</span>}</span>
                      {sg.formattedSuggestion && sg.formattedSuggestion.secondaryText && <span className="auth-addr-sub">{sg.formattedSuggestion.secondaryText}</span>}
                    </div>
                  );
                })}
                </div>
                {businessSearch && formattedAddress.trim().length >= 3 && (
                  <button type="button" className="auth-addr-manual" onMouseDown={(e) => e.preventDefault()} onClick={() => this.startManual(formattedAddress)}>
                    <span className="auth-addr-manual-plus">+</span>
                    <span>Can't see it? Add <strong>{formattedAddress.trim()}</strong> yourself</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </PlacesAutocomplete>
    );
  }

  // Manual entry: carry what they typed across as the business name.
  // Manual entry: carry what they typed across as the name (or the postcode).
  startManual = (typed, prefillPostcode = '') => {
    // If what they typed is a postcode (with or without the space), it belongs
    // in the postcode field, not the business name.
    const raw = (typed || '').trim();
    const compact = raw.replace(/\s+/g, '').toUpperCase();
    const typedIsPostcode = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(compact);
    const postcode = prefillPostcode || (typedIsPostcode ? `${compact.slice(0, -3)} ${compact.slice(-3)}` : '');
    this.setState({ entryMode: 'manual', salonName: typedIsPostcode ? '' : raw, nameFromListing: false, formattedAddress: '', addressPicked: false, addressPrecise: false, businessChosen: false, editingAddress: false, formattedAddressError: '', salonNameError: '', postcodePick: null, postcodeManual: postcode });
  };

  manualPostcodeOk() {
    const pc = this.state.postcodeManual.trim();
    return this.state.countryCode === 'IE' ? IE_EIRCODE.test(pc) || pc.length >= 3 : UK_POSTCODE.test(pc);
  }

  // Fold the manual form into the fields the API takes; coordinates are
  // looked up best-effort and fall back to the UK default.
  composeManualAddress() {
    const s = this.state;
    const country = COUNTRIES.find((c) => c.code === s.countryCode) || COUNTRIES[0];
    const line1 = [s.building.trim(), s.street.trim()].filter(Boolean).join(' ');
    const postcode = s.postcodeManual.trim().toUpperCase().replace(/\s+/, ' ');
    const formatted = [line1, s.addressLine2.trim(), s.town.trim(), postcode, country.name].filter(Boolean).join(', ');
    this.setState({
      formattedAddress: formatted,
      postalCode: postcode,
      cityName: s.town.trim(),
      country: country.code, countryShortName: country.code, countryName: country.name, countryLongName: country.name,
      ...currencyFor(country.code),
    });
    // geocodeByAddress throws straight away when Google hasn't loaded, which
    // would stop Continue on the very path that's there for that case.
    try {
      geocodeByAddress(formatted)
        .then((results) => getLatLng(results[0]))
        .then((latLng) => this.setState({ latitude: latLng.lat, longitude: latLng.lng }))
        .catch(() => { /* keep defaults */ });
    } catch (e) { /* no Google: keep defaults */ }
  }

  resetBusinessSearch = () => {
    this.setState({ formattedAddress: '', addressPicked: false, addressPrecise: false, businessChosen: false, editingAddress: false, salonName: '', nameFromListing: false, salonNameError: '', formattedAddressError: '', postalCode: '', cityName: '', postcodePick: null, building: '', street: '', addressLine2: '', town: '', postcodeManual: '' });
  };

  renderStep1() {
    const { entryMode, salonName, formattedAddress, addressPicked, businessChosen, postcodePick, salonNameError, formattedAddressError } = this.state;
    const manual = entryMode === 'manual';
    const canContinue = this.stepComplete(1);
    const hero = !manual && !businessChosen;
    const collapsed = !hero || this.state.searchFocused || !!formattedAddress.trim();

    return (
      <form className={`auth-form auth-step1${hero ? ' has-hero' : ''}${collapsed ? ' is-collapsed' : ''}`} onSubmit={(e) => { e.preventDefault(); this.handleNext(); }} noValidate>
        {hero && (
          <div className="auth-illo" aria-hidden="true">
            <img src={require('../../assets/images/signup-shopfront.png')} alt="" />
          </div>
        )}
        <h1 className="auth-step-h1">Find your business</h1>
        <p className="auth-step-sub">{manual ? 'Enter your business details.' : 'Search by name or postcode.'}</p>

        {!manual && !businessChosen && (
          <div className="auth-form-group">
            <label className="auth-label" htmlFor="bizSearch">Your salon, barbershop or studio</label>
            {this.renderAddressSearch({ id: 'bizSearch', placeholder: 'Business name or postcode', businessSearch: true, autoFocus: false })}
            {formattedAddressError && <div className="auth-error"><span>{formattedAddressError}</span></div>}
            {postcodePick && (
              <div className="auth-nearby" role="listbox" aria-label={`Businesses at ${postcodePick.postcode}`}>
                <div className="auth-nearby-head">{postcodePick.loading ? `Looking around ${postcodePick.postcode}…` : postcodePick.businesses.length ? `Businesses at ${postcodePick.postcode}` : `No salons or barbers found at ${postcodePick.postcode}`}</div>
                {postcodePick.businesses.map((b) => (
                  <button type="button" key={b.placeId} className="auth-nearby-item" role="option" onClick={() => this.pickNearbyBusiness(b)}>
                    <span className="auth-addr-main">{b.name}</span>
                    {b.address && <span className="auth-addr-sub">{b.address}</span>}
                  </button>
                ))}
                {!postcodePick.loading && (
                  <button type="button" className="auth-addr-manual" onClick={() => this.startManual('', postcodePick.postcode)}>
                    <span className="auth-addr-manual-plus">+</span>
                    <span>{postcodePick.businesses.length ? 'Not listed? Enter your address' : 'Enter your address'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {!manual && businessChosen && (
          <div className="auth-form-group">
            <div className="auth-biz-card">
              <div className="auth-biz-card-row">
                <span className="auth-biz-card-label">Business name</span>
                <input type="text" id="salonName" name="salonName" className={`auth-input auth-input--compact ${salonNameError ? 'error' : ''}`} placeholder="Business name" value={salonName} onChange={this.handleChange} autoComplete="organization" />
              </div>
              <div className="auth-biz-card-row">
                <span className="auth-biz-card-label">Address</span>
                <span className="auth-biz-card-value">
                  {addressPicked ? <TickIcon /> : null} <span>{formattedAddress}</span>
                  <button type="button" className="auth-field-link auth-biz-card-edit" onClick={() => this.setState({ entryMode: 'manual', businessChosen: false, editingAddress: false, addressPicked: false, formattedAddress: '' })}>Edit</button>
                </span>
              </div>
              <button type="button" className="auth-link-btn auth-biz-card-change" onClick={this.resetBusinessSearch}>Not right? Search again</button>
            </div>
            {salonNameError && <div className="auth-error"><span>{salonNameError}</span></div>}
          </div>
        )}

        {manual && (
          <>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="salonName">Salon or business name</label>
              <input type="text" id="salonName" name="salonName" className={`auth-input ${salonNameError ? 'error' : ''}`} placeholder="e.g. The Cutting Room" value={salonName} onChange={this.handleChange} autoComplete="organization" autoFocus={!salonName} />
              <div className="auth-field-hint">The name above the door, not your own name.</div>
              {salonNameError && <div className="auth-error"><span>{salonNameError}</span></div>}
            </div>
            <div className="auth-form-row auth-form-row--narrow-first">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="building">Number</label>
                <input type="text" id="building" name="building" className="auth-input" placeholder="12" value={this.state.building} onChange={this.handleChange} autoComplete="off" autoFocus={!!salonName} />
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="street">Street</label>
                <input type="text" id="street" name="street" className="auth-input" placeholder="High Street" value={this.state.street} onChange={this.handleChange} autoComplete="address-line1" />
              </div>
            </div>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="addressLine2">Address line 2 <span className="auth-label-optional">(optional)</span></label>
              <input type="text" id="addressLine2" name="addressLine2" className="auth-input" placeholder="Unit, floor or area" value={this.state.addressLine2} onChange={this.handleChange} autoComplete="address-line2" />
            </div>
            <div className="auth-form-row">
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="town">Town or city</label>
                <input type="text" id="town" name="town" className="auth-input" placeholder="Manchester" value={this.state.town} onChange={this.handleChange} autoComplete="address-level2" />
              </div>
              <div className="auth-form-group">
                <label className="auth-label" htmlFor="postcodeManual">Postcode</label>
                <input type="text" id="postcodeManual" name="postcodeManual" className={`auth-input ${this.state.postcodeManual && !this.manualPostcodeOk() ? 'error' : ''}`} placeholder="M3 4LY" value={this.state.postcodeManual} onChange={this.handleChange} autoComplete="postal-code" autoCapitalize="characters" />
              </div>
            </div>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="countryCode">Country</label>
              <div className="auth-select-wrap">
                <select id="countryCode" name="countryCode" className="auth-input auth-select" value={this.state.countryCode} onChange={this.handleChange} autoComplete="country">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              {formattedAddressError && <div className="auth-error"><span>{formattedAddressError}</span></div>}
              <button type="button" className="auth-field-link" onClick={() => this.setState({ entryMode: 'search', formattedAddress: '', addressPicked: false, salonName: '', nameFromListing: false, formattedAddressError: '' })}>Search for your business instead</button>
            </div>
          </>
        )}

        <div className="auth-actions">
          <button type="submit" className="auth-button auth-button-primary" disabled={!canContinue} aria-disabled={!canContinue}>Continue</button>
        </div>
      </form>
    );
  }

  renderNav(step) {
    const ok = this.stepComplete(step);
    return (
      <div className="auth-button-row auth-actions">
        <button type="button" className="auth-button auth-button-secondary" onClick={this.handleBack}>Back</button>
        <button type="submit" className="auth-button auth-button-primary" disabled={!ok} aria-disabled={!ok}>Continue</button>
      </div>
    );
  }

  renderStepType() {
    const { businessType, businessTypeError, salonName } = this.state;
    return (
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); this.handleNext(); }} noValidate>
        <h1 className="auth-step-h1">What kind of business is {salonName && salonName.length <= 24 ? salonName : 'it'}?</h1>
        <p className="auth-step-sub">Pick the closest match. It helps clients find you.</p>
        <div className="auth-type-grid" role="radiogroup" aria-label="Business type">
          {BUSINESS_TYPES.map((t) => (
            <button key={t.id} type="button" role="radio" aria-checked={businessType === t.id} className={`auth-type${businessType === t.id ? ' is-on' : ''}`} onClick={() => this.setState({ businessType: t.id, businessTypeError: '' })}>
              <span className="auth-type-check" aria-hidden="true"><TickIcon /></span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
        {businessTypeError && <div className="auth-error"><span>{businessTypeError}</span></div>}
        {this.renderNav(2)}
      </form>
    );
  }

  renderStepName() {
    const { first_name, last_name, fNameError, lNameError } = this.state;
    return (
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); this.handleNext(); }} noValidate>
        <h1 className="auth-step-h1">What's your name?</h1>
        <p className="auth-step-sub">As your clients and team will see it.</p>
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="first_name">First name</label>
          <input type="text" id="first_name" name="first_name" className={`auth-input ${fNameError ? 'error' : ''}`} value={first_name} onChange={this.handleChange} autoComplete="given-name" autoFocus />
          {fNameError && <div className="auth-error"><span>{fNameError}</span></div>}
        </div>
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="last_name">Last name</label>
          <input type="text" id="last_name" name="last_name" className={`auth-input ${lNameError ? 'error' : ''}`} value={last_name} onChange={this.handleChange} autoComplete="family-name" />
          {lNameError && <div className="auth-error"><span>{lNameError}</span></div>}
        </div>
        {this.renderNav(3)}
      </form>
    );
  }

  renderStepContact() {
    const { email, phoneNumber, phoneKey, emailError, phoneNumberError } = this.state;
    return (
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); this.handleNext(); }} noValidate>
        <h1 className="auth-step-h1">How can we reach you?</h1>
        <p className="auth-step-sub">Your email is your sign-in. We text a code to your mobile to confirm it's you.</p>
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="email">Email</label>
          <input type="email" id="email" name="email" className={`auth-input ${emailError ? 'error' : ''}`} placeholder="you@example.com" value={email} onChange={this.handleChange} autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck="false" autoFocus />
          {emailError && <div className="auth-error"><span>{emailError}</span></div>}
        </div>
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="phoneNumber">Mobile number</label>
          <PhoneInput
            key={phoneKey}
            id="phoneNumber"
            international
            defaultCountry="GB"
            countryCallingCodeEditable={false}
            autoComplete="tel"
            placeholder="+44 7700 900000"
            value={phoneNumber}
            onChange={(value) => this.setState({ phoneNumber: dropTrunkZero(value) || '', phoneNumberError: '' })}
            onBlur={() => this.setState((st) => ({ phoneKey: st.phoneKey + 1 }))}
            className={`auth-phone ${phoneNumberError ? 'auth-phone--error' : ''}`}
          />
          {phoneNumberError && <div className="auth-error"><span>{phoneNumberError}</span></div>}
        </div>
        {this.renderNav(4)}
      </form>
    );
  }

  renderStepPassword() {
    const { password, showPassword, passwordStrength, passwordError } = this.state;
    const strength = this.getPasswordStrengthClass();
    return (
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); this.handleNext(); }} noValidate>
        <h1 className="auth-step-h1">Create a password</h1>
        <p className="auth-step-sub">At least 8 characters. A mix of letters, numbers and symbols is strongest.</p>
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="password">Password</label>
          <div className="auth-password">
            <input type={showPassword ? 'text' : 'password'} id="password" name="password" className={`auth-input auth-input--with-action ${passwordError ? 'error' : ''}`} value={password} onChange={this.handleChange} autoComplete="new-password" autoFocus />
            <button type="button" className="auth-password-toggle" onClick={() => this.setState((s) => ({ showPassword: !s.showPassword }))} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword}>
              <EyeIcon off={showPassword} />
            </button>
          </div>
          {password && passwordStrength > 0 && (
            <div className="auth-strength">
              <div className="auth-password-strength"><div className={`auth-password-strength-bar ${strength}`} /></div>
              <span className="auth-strength-label">{strength === 'weak' ? 'Weak' : strength === 'medium' ? 'Good' : 'Strong'}</span>
            </div>
          )}
          {passwordError && <div className="auth-error"><span>{passwordError}</span></div>}
        </div>
        {this.renderNav(5)}
      </form>
    );
  }

  renderStepPlan() {
    const { planKind, selectedPlanType, agree, agreeError } = this.state;
    const isYearly = selectedPlanType === 'yearly';
    return (
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); this.handleNext(); }} noValidate>
        <h1 className="auth-step-h1">Choose your plan</h1>
        <p className="auth-step-sub">Free trial on both. No charge today, cancel any time.</p>

        <div className="auth-billing-toggle" role="radiogroup" aria-label="Billing period">
          <button type="button" role="radio" aria-checked={!isYearly} className={!isYearly ? 'is-on' : ''} onClick={() => this.setState({ selectedPlanType: 'monthly' })}>Monthly</button>
          <button type="button" role="radio" aria-checked={isYearly} className={isYearly ? 'is-on' : ''} onClick={() => this.setState({ selectedPlanType: 'yearly' })}>Annual <em>Save up to 17%</em></button>
        </div>

        <div className="auth-plans" role="radiogroup" aria-label="Plan">
          {PLANS.map((p) => {
            const price = PRICING[p.id];
            const on = planKind === p.id;
            return (
              <button key={p.id} type="button" role="radio" aria-checked={on} className={`auth-plan${on ? ' is-on' : ''}`} onClick={() => this.setState({ planKind: p.id })}>
                <span className="auth-plan-radio" aria-hidden="true" />
                <span className="auth-plan-body">
                  <span className="auth-plan-title">{p.title}</span>
                  <span className="auth-plan-sub">{p.sub}</span>
                  <ul className="auth-plan-points">{p.points.map((pt) => <li key={pt}><TickIcon />{pt}</li>)}</ul>
                </span>
                <span className="auth-plan-side">
                  <span className="auth-plan-price"><strong>£{isYearly ? price.yearlyPerMonth : price.monthly}</strong><span>/month</span></span>
                  <span className="auth-plan-note">{isYearly ? <>£{price.yearly} a year<br />save {price.save}</> : 'Billed monthly'}</span>
                </span>
              </button>
            );
          })}
        </div>

        <label className={`auth-check${agreeError ? ' has-error' : ''}`}>
          <input type="checkbox" name="agree" checked={agree} onChange={this.handleChange} />
          <span className="auth-check-box" aria-hidden="true"><TickIcon /></span>
          <span className="auth-check-text">
            I agree to the <Link to="/partner-terms" className="auth-link" target="_blank" rel="noopener noreferrer">Partner Terms</Link>, <Link to="/beu-pay-terms" className="auth-link" target="_blank" rel="noopener noreferrer">Beu Pay Terms</Link> and <Link to="/privacy" className="auth-link" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
          </span>
        </label>
        {agreeError && <div className="auth-error"><span>{agreeError}</span></div>}

        {this.renderNav(6)}
      </form>
    );
  }

  renderStepVerify() {
    const { phoneNumber, codeArr, codeError, codeSent, sendingCode, verifying, resendCountdown, registered, isLoading, error } = this.state;
    const masked = String(phoneNumber || '').replace(/.(?=.{3})/g, '•');
    const code = (codeArr || []).join('');

    if (registered) {
      return (
        <div className="auth-step">
          <h1 className="auth-step-h1">{isLoading ? 'Taking you to payment' : 'Your account is ready'}</h1>
          <p className="auth-step-sub">{isLoading ? 'One moment. Stripe handles the card details securely.' : 'Set up your subscription to start taking bookings.'}</p>
          {error && <div className="auth-error auth-mb-md"><span>{error}</span></div>}
          <div className="auth-actions">
            <button type="button" className={`auth-button auth-button-primary ${isLoading ? 'auth-button-loading' : ''}`} onClick={this.handlePayment} disabled={isLoading}>Continue to payment</button>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-step">
        <h1 className="auth-step-h1">Verify your number</h1>
        <p className="auth-step-sub">{codeSent ? <>We texted a 6-digit code to <strong>{masked}</strong>.</> : <>Sending a 6-digit code to <strong>{masked}</strong>…</>}</p>

        <div className="auth-form-group">
          <div className="auth-otp">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <input
                key={i}
                ref={(el) => { this.codeInputs[i] = el; }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={codeArr[i] || ''}
                onChange={(e) => this.handleCodeChange(i, e)}
                onKeyDown={(e) => this.handleCodeKeyDown(i, e)}
                onPaste={this.handleCodePaste}
                autoFocus={i === 0}
                aria-label={`Digit ${i + 1}`}
                className={`auth-otp-box${codeArr[i] ? ' filled' : ''}${codeError ? ' error' : ''}`}
              />
            ))}
          </div>
          {codeError && <div className="auth-error"><span>{codeError}</span></div>}
        </div>

        <div className="auth-otp-links">
          {resendCountdown > 0
            ? <span>Resend code in 0:{String(resendCountdown).padStart(2, '0')}</span>
            : <button type="button" className="auth-link-btn" onClick={this.handleSendCode} disabled={sendingCode}>{sendingCode ? 'Sending…' : 'Resend code'}</button>}
          <span className="auth-otp-sep">·</span>
          <button type="button" className="auth-link-btn" onClick={this.wrongNumber}>Wrong number?</button>
        </div>

        <div className="auth-actions">
          <button type="button" onClick={this.handleVerifyAndRegister} className={`auth-button auth-button-primary ${verifying ? 'auth-button-loading' : ''}`} disabled={verifying || code.length !== 6}>
            {!verifying && 'Verify and continue'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { currentStep, error, registered } = this.state;
    return (
      <AuthLayout rightPanel={<Carousel />}>
        <div className="auth-form-container auth-signup" ref={(el) => { this._rootEl = el; }}>
          {/* Header: back button and the step progress on one row. */}
          <div className="auth-signup-head">
            {registered
              ? <Link to="/signup" className="auth-nav-back" aria-label="Back"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></Link>
              : <button type="button" className="auth-nav-back" onClick={this.handleBack} aria-label="Back"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></button>}
            {this.renderProgress()}
          </div>

          {error && !registered && <div className="auth-error auth-mb-md"><span>{error}</span></div>}

          {currentStep === 1 && this.renderStep1()}
          {currentStep === 2 && this.renderStepType()}
          {currentStep === 3 && this.renderStepName()}
          {currentStep === 4 && this.renderStepContact()}
          {currentStep === 5 && this.renderStepPassword()}
          {currentStep === 6 && this.renderStepPlan()}
          {currentStep === 7 && this.renderStepVerify()}

          {!registered && (
            <div className="auth-footer-link">
              <span className="auth-text-muted">Already have an account? </span>
              <Link to="/login" className="auth-link">Sign in</Link>
            </div>
          )}
        </div>
      </AuthLayout>
    );
  }
}

function mapStateToProps() {
  return {};
}

export default connect(mapStateToProps)(ProfessionalSignup);
